/* ──────────────────────────────────────────────────────────────
   src/pages/admin/agenda/ReunionReglasPage.tsx
   Página de gestión de reglas de agendación de reuniones
   ────────────────────────────────────────────────────────────── */
import React, { useMemo, useState } from 'react';
import { Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import ReunionReglasTable from '../../../components/reuniones/ReunionReglasTable';
import ReunionReglaFormDialog from '../../../components/reuniones/ReunionReglaFormDialog';

import {
  useReunionReglasList,
  useCreateReunionRegla,
  useUpdateReunionRegla,
  useDeleteReunionRegla,
  reglaToFormValues,
  formValuesToPayload,
  type ReunionReglaFormValues,
} from '../../../hooks/useReunionReglas';

import {
  REUNION_TIPO_VALUES,
  REUNION_ROL_DESTINO_VALUES,
  REUNION_CANAL_VALUES,
  type ReunionReglaAgendacion,
  type ReunionReglaFilters,
} from '../../../api/reunionReglas.api';

export default function ReunionReglasPage() {
  const { t } = useTranslation<'translation'>();

  /* ========= filtros ========= */
  const [filters, setFilters] = useState<ReunionReglaFilters>({});

  const handleFilterChange = <K extends keyof ReunionReglaFilters>(
    key: K,
    value: ReunionReglaFilters[K],
  ) => {
    setFilters((prev: ReunionReglaFilters) => ({
      ...prev,
      [key]: (value as any) === '' ? undefined : value,
    }));
  };

  const { rows, loading } = useReunionReglasList(filters);

  /* ========= diálogos / CRUD ========= */
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    current?: ReunionReglaAgendacion | null;
  }>({ open: false, mode: 'create', current: null });

  const createMut = useCreateReunionRegla();
  const updateMut = useUpdateReunionRegla();
  const deleteMut = useDeleteReunionRegla();

  const handleNew = () =>
    setDialogState({
      open: true,
      mode: 'create',
      current: null,
    });

  const handleEdit = (row: ReunionReglaAgendacion) =>
    setDialogState({
      open: true,
      mode: 'edit',
      current: row,
    });

  const handleCloseDialog = () => setDialogState((p) => ({ ...p, open: false }));

  const handleSubmitDialog = async (values: ReunionReglaFormValues) => {
    const payload = formValuesToPayload(values);
    if (dialogState.mode === 'create') {
      await createMut.mutateAsync(payload);
    } else if (dialogState.mode === 'edit' && dialogState.current) {
      await updateMut.mutateAsync({
        id: dialogState.current.id,
        payload,
      });
    }
  };

  const handleDelete = async (row: ReunionReglaAgendacion) => {
    const ok = window.confirm(
      String(t('reunionRules.confirmDelete', '¿Seguro que quieres eliminar esta regla?')),
    );
    if (!ok) return;
    await deleteMut.mutateAsync(row.id);
  };

  const formInitialValues = useMemo(
    () => reglaToFormValues(dialogState.current),
    [dialogState.current],
  );

  const anyLoading = loading || createMut.isPending || updateMut.isPending || deleteMut.isPending;

  return (
    <Stack spacing={2}>
      {/* Cabecera */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">
          {String(t('reunionRules.title', 'Reglas de agendación de reuniones'))}
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{
            backgroundColor: '#21a598',
            '&:hover': { backgroundColor: '#1a8578' },
            boxShadow: 'none',
          }}
        >
          {String(t('reunionRules.new', 'Nueva regla'))}
        </Button>
      </Stack>

      {/* Filtros */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2}>
          {/* Tipo de reunión */}
          <TextField
            select
            size="small"
            sx={{ minWidth: 180 }}
            label={String(t('reunionRules.filters.type', 'Tipo de reunión'))}
            value={filters.tipoReunion ?? ''}
            onChange={(e) => handleFilterChange('tipoReunion', e.target.value as string)}
          >
            <MenuItem value="">{String(t('common.all', 'Todos'))}</MenuItem>
            {REUNION_TIPO_VALUES.map((v: string) => (
              <MenuItem key={v} value={v}>
                {String(t(`reunionRules.type.${v}`, v))}
              </MenuItem>
            ))}
          </TextField>

          {/* Rol destino */}
          <TextField
            select
            size="small"
            sx={{ minWidth: 180 }}
            label={String(t('reunionRules.filters.role', 'Rol destino'))}
            value={filters.rolStaffDestino ?? ''}
            onChange={(e) => handleFilterChange('rolStaffDestino', e.target.value as string)}
          >
            <MenuItem value="">{String(t('common.all', 'Todos'))}</MenuItem>
            {REUNION_ROL_DESTINO_VALUES.map((v: string) => (
              <MenuItem key={v} value={v}>
                {String(t(`reunionRules.role.${v}`, v))}
              </MenuItem>
            ))}
          </TextField>

          {/* Canal preferente */}
          <TextField
            select
            size="small"
            sx={{ minWidth: 180 }}
            label={String(t('reunionRules.filters.channel', 'Canal preferente'))}
            value={filters.canalPreferente ?? ''}
            onChange={(e) => handleFilterChange('canalPreferente', e.target.value as string)}
          >
            <MenuItem value="">{String(t('common.all', 'Todos'))}</MenuItem>
            {REUNION_CANAL_VALUES.map((v: string) => (
              <MenuItem key={v} value={v}>
                {String(t(`reunionRules.channel.${v}`, v))}
              </MenuItem>
            ))}
          </TextField>

          {/* Activa / inactiva */}
          <TextField
            select
            size="small"
            sx={{ minWidth: 180 }}
            label={String(t('reunionRules.filters.active', 'Estado'))}
            value={filters.activa == null ? '' : filters.activa ? '1' : '0'}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') {
                handleFilterChange('activa', undefined);
              } else {
                handleFilterChange('activa', v === '1');
              }
            }}
          >
            <MenuItem value="">{String(t('common.all', 'Todas'))}</MenuItem>
            <MenuItem value="1">{String(t('reunionRules.active.yes', 'Solo activas'))}</MenuItem>
            <MenuItem value="0">
              {String(t('reunionRules.active.noOnly', 'Solo inactivas'))}
            </MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {/* Tabla */}
      <Box>
        <ReunionReglasTable
          rows={rows}
          loading={anyLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Box>

      {/* Diálogo create/edit */}
      <ReunionReglaFormDialog
        open={dialogState.open}
        mode={dialogState.mode}
        initialValues={formInitialValues}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
      />
    </Stack>
  );
}
